import React, { useState, useEffect } from 'react';
import { Loader2, Check, AlertCircle, ReceiptText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import {
  getRetailerBillParsingPrompts,
  saveRetailerBillParsingPrompts,
  BILL_SECTION_KEYS,
  type BillSectionKey,
} from '../../services/retailerService';

type BillPromptsTabProps = {
  retailer: { id: string; name: string };
};

const SECTION_LABEL: Record<BillSectionKey, string> = {
  retail: 'Retail energy',
  distribution: 'Network / distribution',
  metering: 'Metering',
  environmental: 'Environmental',
  other: 'Other',
  gst: 'GST',
};

/**
 * Per-retailer prompts for BILL VALIDATION invoice extraction.
 *
 * Sibling of `PromptsTab`, which does the same job for tender offers. They are
 * separate components against separate columns and separate endpoints:
 *
 *   offers → offer_parsing_prompt / section_parsing_prompts / flag_active
 *   bills  → bill_parsing_prompt  / bill_section_parsing_prompts / bill_prompt_active
 *
 * Switching a retailer's bill prompt on therefore cannot switch its tender offer
 * prompt on, which is what would have happened had `flag_active` been reused.
 *
 * The section list is `BILL_SECTION_KEYS`, deliberately not the tender
 * `SectionKey` values — the two vocabularies are unrelated and mixing them would
 * silently produce prompts the parser never reads.
 *
 * Nothing here is retroactive: editing a prompt does not touch any invoice that
 * has already been parsed. Only a re-parse applies a change.
 */
export const BillPromptsTab: React.FC<BillPromptsTabProps> = ({ retailer }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [active, setActive] = useState(false);
  const [fullPrompt, setFullPrompt] = useState('');
  const [sectionPrompts, setSectionPrompts] = useState<Partial<Record<BillSectionKey, string>>>({});

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    void (async () => {
      const result = await getRetailerBillParsingPrompts(retailer.id);
      if (!mounted) return;
      if (result.success && result.data) {
        setActive(result.data.bill_prompt_active ?? false);
        setFullPrompt(result.data.bill_parsing_prompt || '');
        setSectionPrompts(result.data.bill_section_parsing_prompts || {});
        setError(null);
      } else {
        setError(result.error || 'Could not load this retailer’s bill prompts');
      }
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [retailer.id]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const result = await saveRetailerBillParsingPrompts(retailer.id, {
      bill_prompt_active: active,
      bill_parsing_prompt: fullPrompt.trim() || null,
      bill_section_parsing_prompts:
        BILL_SECTION_KEYS.some((k) => sectionPrompts[k]?.trim()) ? sectionPrompts : null,
    });
    if (result.success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } else {
      setError(result.error || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading bill prompts…
      </div>
    );
  }

  const enabledButEmpty = active && !fullPrompt.trim();

  return (
    <div className="space-y-6 max-w-4xl">

      {error && (
        <div className="flex gap-2 items-start rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 break-words">{error}</p>
        </div>
      )}

      <Card>
        <CardContent className="flex items-center justify-between py-4">
          <div>
            <p className="text-sm font-medium">Use a custom bill parsing prompt</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {active
                ? 'Enabled — invoices parsed with this retailer selected use the prompt below.'
                : 'Disabled — invoices use the standard prompt. Anything written below is kept but not used.'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Independent of the offer-parsing switch on the previous tab.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setActive((v) => !v)}
            role="switch"
            aria-checked={active}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${active ? 'bg-primary' : 'bg-muted'}`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform ${active ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </CardContent>
      </Card>

      {enabledButEmpty && (
        <div className="flex gap-2 items-start rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            Switched on, but no prompt is written — invoices will still parse on the standard prompt.
          </p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ReceiptText className="h-4 w-4" /> Full Bill Parsing Prompt
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Replaces the standard invoice-extraction system prompt for <strong>{retailer.name}</strong>.
            It must still ask for the same JSON shape the parser expects — this overrides the
            instructions, not the output contract.
          </p>
        </CardHeader>
        <CardContent>
          <textarea
            value={fullPrompt}
            onChange={(e) => setFullPrompt(e.target.value)}
            placeholder="Leave empty to use the standard bill parsing prompt…"
            rows={14}
            className="w-full font-mono text-xs border rounded-md p-3 resize-y bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-muted-foreground">
              {fullPrompt.length.toLocaleString()} characters
            </span>
            {fullPrompt && (
              <button
                type="button"
                onClick={() => setFullPrompt('')}
                className="text-xs text-muted-foreground hover:text-destructive"
              >
                Clear
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-Section Overrides</CardTitle>
          <p className="text-xs text-muted-foreground">
            Appended when someone re-parses only these sections of an invoice. These are the bill
            validation sections — not the tender offer sections.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {BILL_SECTION_KEYS.map((key) => (
            <div key={key}>
              <label className="block text-xs font-medium text-muted-foreground mb-1 capitalize">
                {SECTION_LABEL[key]} <span className="font-mono opacity-60">({key})</span>
              </label>
              <textarea
                value={sectionPrompts[key] ?? ''}
                onChange={(e) => setSectionPrompts((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={`Extra instructions when re-parsing only ${SECTION_LABEL[key].toLowerCase()}…`}
                rows={3}
                className="w-full font-mono text-xs border rounded-md p-2.5 resize-y bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={() => void handleSave()} disabled={saving} className="gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {saving ? 'Saving…' : 'Save bill prompts'}
        </Button>
        {saved && <span className="text-xs text-emerald-600 font-medium">Saved</span>}
        <span className="text-xs text-muted-foreground ml-auto">
          Existing parsed invoices are unaffected — only a re-parse applies a change.
        </span>
      </div>
    </div>
  );
};

export default BillPromptsTab;
